import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { FilterUtils, PaginationUtils } from '@ocean/utils'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getActivityLog } from 'modules/activityLog/ActivityLog/api/activityLogService'
import {
  ActivityLogExternalFilterItem,
  ActivityLogItem,
  ActivityLogInternalFilterItem,
  ActivityLogExternalFilterItems,
  ActivityLogInternalFilterItems,
} from 'modules/activityLog/ActivityLog/types'
import { ActivityLogState, ApiUserActivityLogResponse } from './types'

const initialState: ActivityLogState = {
  userActivityLog: {},
  activityLogExternalFilters: {
    trainingTypeFilter: { keyType: FilterKeyType.Array },
    dateFrom: { keyType: FilterKeyType.String },
    dateTo: { keyType: FilterKeyType.String },
  },
  activityLogInternalFilters: {
    trainingName: { keyType: FilterKeyType.String },
  },
}

const activityLogSlice = createSlice({
  name: 'activityLog',
  initialState,
  reducers: {
    activityLogLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiUserActivityLogResponse>>
    ) {
      state.userActivityLog = action.payload
    },
    clearUserActivityLog(state) {
      state.userActivityLog = initialState.userActivityLog
    },
    setActivityLogExternalFilters(state, action: PayloadAction<ActivityLogExternalFilterItem>) {
      const filter = state.activityLogExternalFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
    setActivityLogInternalFilters(state, action: PayloadAction<ActivityLogInternalFilterItem>) {
      const filter = state.activityLogInternalFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

export const {
  activityLogLoaded,
  setActivityLogExternalFilters,
  setActivityLogInternalFilters,
  clearUserActivityLog,
} = activityLogSlice.actions
export default activityLogSlice.reducer

export const getFilteredActivityLog = createSelector(
  (activityLog: ActivityLogItem[] = [], internalFilters: ActivityLogInternalFilterItems) => ({
    activityLog,
    internalFilters,
  }),
  ({ activityLog, internalFilters }) => FilterUtils.filterData(internalFilters, activityLog)
)

// TODO: Check miss userActivityLog error
export const getActivityLogPageItems = createSelector(
  (
    activityLog: ActivityLogItem[] = [],
    internalFilters: ActivityLogInternalFilterItems,
    currentPageId: number,
    pageSize: number
  ) => ({
    activityLog: getFilteredActivityLog(activityLog, internalFilters),
    currentPageId,
    pageSize,
  }),
  ({ activityLog, currentPageId, pageSize }) =>
    PaginationUtils.getPageItems<ActivityLogItem>(activityLog, currentPageId, pageSize)
)

export const loadActivityLogData = (externalFilters: ActivityLogExternalFilterItems) => {
  return createApiAction({
    apiCalls: {
      activityLog: () => getActivityLog(externalFilters),
    },
    payloadAction: activityLogLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ activityLog }) => activityLog.userActivityLog.result,
  })
}
